import { useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocalizedText } from '../../lib/localizedText'
import ConfirmDialog, { confirmDialog } from '../../components/ui/ConfirmDialog'
import { Menu } from 'primereact/menu'
import type { MenuItem } from 'primereact/menuitem'
import Button from '../../components/ui/Button'
import DataTable from '../../components/ui/DataTable'
import EmptyState from '../../components/ui/EmptyState'
import ExcelExportButton from '../../components/ui/ExcelExportButton'
import FilterBar from '../../components/ui/FilterBar'
import InfoBanner from '../../components/ui/InfoBanner'
import PageHeader from '../../components/ui/PageHeader'
import RefreshButton from '../../components/ui/RefreshButton'
import { useToast } from '../../components/ui/ToastProvider'
import { getApiErrorCode, isOptimisticLock } from '../../api/apiError'
import { countryColumns } from './components/CountryColumns'
import { cityColumns } from './components/CityColumns'
import CountryFormDialog from './components/CountryFormDialog'
import CityFormDialog from './components/CityFormDialog'
import { exportCountries } from './api/countryApi'
import { exportCities } from './api/cityApi'
import { useCountries } from './hooks/useCountries'
import { useCities } from './hooks/useCities'
import type { City, CityUpsert, Country, CountryUpsert } from './types'
import Badge from '../../components/ui/Badge'
import { InputSwitch } from 'primereact/inputswitch'
import { CARD_VIEW_BREAKPOINT, useMediaQuery } from '../../lib/useMediaQuery'
import { isCardBodyClick } from '../../lib/cardClick'
// Layout sınıfları (admin-page/admin-form/admin-row-actions/admin-switch) paylaşılan — 012'den yeniden kullanılır.
import '../admin/admin.css'

/** Mobil kart kebab menüsü — switch dışındaki Düzenle/Sil'i tek düğmede toplar (v2 madde 1). */
function LookupCardMenu<T>({
  row,
  busy,
  editLabel,
  deleteLabel,
  menuLabel,
  onEdit,
  onDelete,
}: Readonly<{
  row: T
  busy: boolean
  editLabel: string
  deleteLabel: string
  menuLabel: string
  onEdit: (row: T) => void
  onDelete: (row: T) => void
}>) {
  const menuRef = useRef<Menu>(null)
  const items: MenuItem[] = [
    { label: editLabel, icon: 'pi pi-pencil', command: () => onEdit(row) },
    {
      label: deleteLabel,
      icon: 'pi pi-trash',
      className: 'ui-card-menu-item--danger',
      command: () => onDelete(row),
    },
  ]
  return (
    <>
      <Menu model={items} popup ref={menuRef} appendTo={document.body} />
      <Button
        icon="pi pi-ellipsis-v"
        rounded
        text
        disabled={busy}
        aria-label={menuLabel}
        onClick={(e) => menuRef.current?.toggle(e)}
      />
    </>
  )
}

/**
 * Etkinlik Takip → Ülke yönetim ekranı (026). Ana-detay (parent-child): üstte **Ülke** listesi
 * (`selectionMode="single"`), altta seçili ülkenin **Şehir** listesi — ikisi de paylaşılan ui/DataTable +
 * FormDialog + durum toggle + soft-delete (confirmDialog). REF_COUNTRY/REF_CITY'de SORT_INDEX olmadığından
 * (Adım 0) elle sıra kolonu yoktur; listeler ada göre sıralı + sayfalıdır. Ülke listesi tümü istemcide yüklü
 * olduğundan TR/EN ad kolonları tıklanınca **istemci tarafı sıralanır** ve üstte **ada göre arama** (TR-farkında,
 * TR+EN içinde) client-side filtreler. Ülke seçili değilken şehir kartı nötr yönlendirme gösterir ve şehir
 * "Yeni ekle" pasiftir. Başlık = menü adı (`nav.etkinlik.countries`).
 */
export default function CountriesPage() {
  const { t } = useTranslation()
  const localized = useLocalizedText()
  const countries = useCountries()
  const toast = useToast()

  const [selectedCountry, setSelectedCountry] = useState<Country | null>(null)
  const cities = useCities(selectedCountry?.id ?? null)

  // Ada göre arama — tüm ülkeler istemcide yüklü; anlık, TR-farkında (TR+EN) client-side filtre.
  const [countrySearch, setCountrySearch] = useState('')

  const [countryFormOpen, setCountryFormOpen] = useState(false)
  const [editingCountry, setEditingCountry] = useState<Country | null>(null)
  const [countryNameError, setCountryNameError] = useState<string | null>(null)

  const [cityFormOpen, setCityFormOpen] = useState(false)
  const [editingCity, setEditingCity] = useState<City | null>(null)
  const [cityNameError, setCityNameError] = useState<string | null>(null)

  const cityRows = cities.list.data?.content ?? []

  // TR-farkında arama: needle boşsa tüm liste; değilse TR/EN adda (tr-TR büyük harf) alt-dize eşleşmesi.
  const countryContent = countries.list.data?.content
  const filteredCountryRows = useMemo(() => {
    const all = countryContent ?? []
    const needle = countrySearch.trim().toLocaleUpperCase('tr-TR')
    if (!needle) return all
    return all.filter(
      (c) =>
        c.nameTr.toLocaleUpperCase('tr-TR').includes(needle) ||
        (c.nameEn ?? '').toLocaleUpperCase('tr-TR').includes(needle),
    )
  }, [countryContent, countrySearch])
  const countrySubmitting = countries.create.isPending || countries.update.isPending
  const citySubmitting = cities.create.isPending || cities.update.isPending

  const countryBusyId = countries.changeStatus.isPending
    ? (countries.changeStatus.variables?.id ?? null)
    : countries.remove.isPending
      ? (countries.remove.variables ?? null)
      : null
  const cityBusyId = cities.changeStatus.isPending
    ? (cities.changeStatus.variables?.id ?? null)
    : cities.remove.isPending
      ? (cities.remove.variables ?? null)
      : null

  const notify = (severity: 'success' | 'error', summary: string) =>
    toast.show({ severity, summary, life: 3000 })

  // --- Ülke ---

  const openCreateCountry = () => {
    setEditingCountry(null)
    setCountryNameError(null)
    setCountryFormOpen(true)
  }
  const openEditCountry = (row: Country) => {
    setEditingCountry(row)
    setCountryNameError(null)
    setCountryFormOpen(true)
  }

  const handleCountryError = (err: unknown) => {
    if (getApiErrorCode(err) === 'DUPLICATE_NAME') {
      setCountryNameError(t('location.countries.errors.duplicate'))
      return
    }
    if (isOptimisticLock(err)) {
      setCountryFormOpen(false)
      countries.list.refetch()
    }
  }

  const onSubmitCountry = (payload: CountryUpsert) => {
    if (editingCountry) {
      countries.update.mutate(
        { id: editingCountry.id, body: { ...payload, modDate: editingCountry.modDate } },
        {
          onSuccess: (updated) => {
            notify('success', t('location.countries.toast.updated'))
            setCountryFormOpen(false)
            // Seçili ülke düzenlendiyse başlık/şehir-formu bağlamı bayat kalmasın.
            if (selectedCountry?.id === updated.id) setSelectedCountry(updated)
          },
          onError: handleCountryError,
        },
      )
    } else {
      countries.create.mutate(payload, {
        onSuccess: () => {
          notify('success', t('location.countries.toast.created'))
          setCountryFormOpen(false)
        },
        onError: handleCountryError,
      })
    }
  }

  const onToggleCountry = (row: Country, active: boolean) => {
    countries.changeStatus.mutate(
      { id: row.id, body: { recordStatus: active ? 1 : 0, modDate: row.modDate } },
      { onSuccess: () => notify('success', t('location.countries.toast.statusChanged')) },
    )
  }

  const onDeleteCountry = (row: Country) => {
    confirmDialog({
      header: t('location.countries.delete.title'),
      message: t('location.countries.delete.confirm', { name: row.nameTr }),
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      acceptLabel: t('location.countries.delete.accept'),
      accept: () =>
        countries.remove.mutate(row.id, {
          onSuccess: () => {
            notify('success', t('location.countries.toast.deleted'))
            if (selectedCountry?.id === row.id) setSelectedCountry(null)
          },
        }),
    })
  }

  // --- Şehir (seçili ülke kapsamında) ---

  const openCreateCity = () => {
    setEditingCity(null)
    setCityNameError(null)
    setCityFormOpen(true)
  }
  const openEditCity = (row: City) => {
    setEditingCity(row)
    setCityNameError(null)
    setCityFormOpen(true)
  }

  const handleCityError = (err: unknown) => {
    if (getApiErrorCode(err) === 'DUPLICATE_NAME') {
      setCityNameError(t('location.cities.errors.duplicate'))
      return
    }
    if (isOptimisticLock(err)) {
      setCityFormOpen(false)
      cities.list.refetch()
    }
  }

  const onSubmitCity = (payload: CityUpsert) => {
    if (editingCity) {
      cities.update.mutate(
        { id: editingCity.id, body: { ...payload, modDate: editingCity.modDate } },
        {
          onSuccess: () => {
            notify('success', t('location.cities.toast.updated'))
            setCityFormOpen(false)
          },
          onError: handleCityError,
        },
      )
    } else {
      cities.create.mutate(payload, {
        onSuccess: () => {
          notify('success', t('location.cities.toast.created'))
          setCityFormOpen(false)
        },
        onError: handleCityError,
      })
    }
  }

  const onToggleCity = (row: City, active: boolean) => {
    cities.changeStatus.mutate(
      { id: row.id, body: { recordStatus: active ? 1 : 0, modDate: row.modDate } },
      { onSuccess: () => notify('success', t('location.cities.toast.statusChanged')) },
    )
  }

  const onDeleteCity = (row: City) => {
    confirmDialog({
      header: t('location.cities.delete.title'),
      message: t('location.cities.delete.confirm', { name: row.nameTr }),
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      acceptLabel: t('location.cities.delete.accept'),
      accept: () =>
        cities.remove.mutate(row.id, {
          onSuccess: () => notify('success', t('location.cities.toast.deleted')),
        }),
    })
  }

  const citiesTitle = selectedCountry
    ? `${t('location.cities.title')} — ${selectedCountry.nameTr}`
    : t('location.cities.title')
  const isMobile = useMediaQuery(CARD_VIEW_BREAKPOINT)

  return (
    <div className="admin-page">
      <ConfirmDialog />
      <CountryFormDialog
        visible={countryFormOpen}
        initial={editingCountry}
        submitting={countrySubmitting}
        nameError={countryNameError}
        onClearNameError={() => setCountryNameError(null)}
        onSubmit={onSubmitCountry}
        onHide={() => setCountryFormOpen(false)}
      />
      <CityFormDialog
        visible={cityFormOpen}
        initial={editingCity}
        countryName={selectedCountry?.nameTr}
        submitting={citySubmitting}
        nameError={cityNameError}
        onClearNameError={() => setCityNameError(null)}
        onSubmit={onSubmitCity}
        onHide={() => setCityFormOpen(false)}
      />

      {/* Üst kart — Ülke */}
      <PageHeader
        title={t('nav.etkinlik.countries')}
        actions={
          <>
            <RefreshButton onRefresh={() => countries.list.refetch()} />
            <ExcelExportButton
              onExport={() => exportCountries(countrySearch)}
              disabled={filteredCountryRows.length === 0}
            />
            <Button
              icon="pi pi-plus"
              label={t('location.countries.actions.new')}
              onClick={openCreateCountry}
            />
          </>
        }
      />

      {/* Ada göre arama (TR-farkında, TR+EN) — tüm ülkeler istemcide yüklü olduğundan anlık filtreler. */}
      <FilterBar
        searchValue={countrySearch}
        searchPlaceholder={t('location.countries.search')}
        onSearch={setCountrySearch}
      />

      {/* REF_COUNTRY'de SORT_INDEX yok → ada göre sıralı + sayfalı liste; TR/EN kolon başlığı ile sıralanır;
          satır seçimi şehir kartını besler. */}
      {isMobile ? (
        <div className="ui-card-list">
          {countries.list.isLoading &&
            Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="ui-card ui-card--skeleton" aria-hidden />
            ))}
          {!countries.list.isLoading && filteredCountryRows.length === 0 && (
            <EmptyState
              title={t('location.countries.empty.title')}
              description={t('location.countries.empty.desc')}
            />
          )}
          {!countries.list.isLoading &&
            filteredCountryRows.map((row) => {
              const active = row.recordStatus === 1
              const selected = selectedCountry?.id === row.id
              return (
                <div
                  key={row.id}
                  className={`ui-card ui-card--clickable${selected ? ' ui-card--selected' : ''}`}
                  role="button"
                  tabIndex={0}
                  onClick={(e) => {
                    // Eylem alanından (switch/menü) gelen tıklama ülkeyi SEÇMEZ; gerekçe:
                    // `lib/cardClick`.
                    if (isCardBodyClick(e)) setSelectedCountry(row)
                  }}
                  onKeyDown={(e) => {
                    // Yalnız kartın KENDİSİNE gelen Enter/Space ülkeyi seçer: kart içindeki
                    // InputSwitch üzerinde Enter/Space hem durumu değiştirip hem ülkeyi seçiyordu
                    // (click yolu yukarıdaki `isCardBodyClick` ile eleniyor ama keydown yukarı
                    // kabarcıklanıyor). Koruma KARTTA durur; eylem sarmalayıcısı handler TAŞIMAZ —
                    // `0717f6ca` sarmalayıcıdaki click-stopPropagation'ı kaldırmıştı (S1082+S6848),
                    // sarmalayıcıya `onKeyDown` eklemek de aynı S6848 gerekçesiyle yasaktır
                    // (`c2741715` bunu netleştirdi); 31.08.2026'da (bu commit) aynı click temizliği
                    // buraya da uygulandı. Emsal: MeetingGroupCard.
                    if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) {
                      e.preventDefault()
                      setSelectedCountry(row)
                    }
                  }}
                >
                  <div className="ui-card-top">
                    <Badge tone={active ? 'green' : 'neutral'}>
                      {active
                        ? t('location.countries.status.active')
                        : t('location.countries.status.passive')}
                    </Badge>
                    <div className="ui-card-actions ui-card-actions--compact">
                      <InputSwitch
                        className="admin-switch"
                        checked={active}
                        disabled={countryBusyId === row.id}
                        onChange={(e) => onToggleCountry(row, Boolean(e.value))}
                        aria-label={row.nameTr}
                        tooltip={t('location.countries.actions.toggle')}
                        tooltipOptions={{ position: 'bottom' }}
                      />
                      <LookupCardMenu
                        row={row}
                        busy={countryBusyId === row.id}
                        editLabel={t('location.countries.actions.edit')}
                        deleteLabel={t('location.countries.actions.delete')}
                        menuLabel={t('location.countries.actions.menu')}
                        onEdit={openEditCountry}
                        onDelete={onDeleteCountry}
                      />
                    </div>
                  </div>
                  <div className="ui-card-head">
                    <span className="ui-card-title">{localized(row.nameTr, row.nameEn)}</span>
                  </div>
                </div>
              )
            })}
        </div>
      ) : (
        <DataTable
          value={filteredCountryRows}
          loading={countries.list.isLoading}
          selectionMode="single"
          selection={selectedCountry}
          onSelectionChange={(e) => setSelectedCountry(e.value as Country)}
          metaKeySelection={false}
          emptyMessage={
            <EmptyState
              title={t('location.countries.empty.title')}
              description={t('location.countries.empty.desc')}
            />
          }
        >
          {countryColumns({
            t,
            localized,
            busyId: countryBusyId,
            onEdit: openEditCountry,
            onToggle: onToggleCountry,
            onDelete: onDeleteCountry,
          })}
        </DataTable>
      )}

      {/* Alt kart — seçili ülkenin Şehirleri (ülke tablosundan görsel olarak ayrılır) */}
      <div className="location-cities-section">
        <PageHeader
          title={citiesTitle}
          actions={
            <>
              {selectedCountry && (
                <RefreshButton onRefresh={() => cities.list.refetch()} />
              )}
              {/* Şehir dosyası seçili ülkeye bağlı → ülke seçilmeden dışa aktarılacak bir küme yok. */}
              {selectedCountry && (
                <ExcelExportButton
                  onExport={() => exportCities(selectedCountry.id)}
                  disabled={cityRows.length === 0}
                />
              )}
              <Button
                icon="pi pi-plus"
                label={t('location.cities.actions.new')}
                onClick={openCreateCity}
                disabled={!selectedCountry}
              />
            </>
          }
        />

        {selectedCountry ? (
          isMobile ? (
            <div className="ui-card-list">
              {cities.list.isLoading &&
                Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="ui-card ui-card--skeleton" aria-hidden />
                ))}
              {!cities.list.isLoading && cityRows.length === 0 && (
                <EmptyState
                  title={t('location.cities.empty.title')}
                  description={t('location.cities.empty.desc')}
                />
              )}
              {!cities.list.isLoading &&
                cityRows.map((row) => {
                  const active = row.recordStatus === 1
                  return (
                    <div key={row.id} className="ui-card">
                      <div className="ui-card-top">
                        <Badge tone={active ? 'green' : 'neutral'}>
                          {active
                            ? t('location.cities.status.active')
                            : t('location.cities.status.passive')}
                        </Badge>
                        <div className="ui-card-actions ui-card-actions--compact">
                          <InputSwitch
                            className="admin-switch"
                            checked={active}
                            disabled={cityBusyId === row.id}
                            onChange={(e) => onToggleCity(row, Boolean(e.value))}
                            aria-label={row.nameTr}
                            tooltip={t('location.cities.actions.toggle')}
                            tooltipOptions={{ position: 'bottom' }}
                          />
                          <LookupCardMenu
                            row={row}
                            busy={cityBusyId === row.id}
                            editLabel={t('location.cities.actions.edit')}
                            deleteLabel={t('location.cities.actions.delete')}
                            menuLabel={t('location.cities.actions.menu')}
                            onEdit={openEditCity}
                            onDelete={onDeleteCity}
                          />
                        </div>
                      </div>
                      <div className="ui-card-head">
                        <span className="ui-card-title">{localized(row.nameTr, row.nameEn)}</span>
                      </div>
                    </div>
                  )
                })}
            </div>
          ) : (
            <DataTable
              value={cityRows}
              loading={cities.list.isLoading}
              emptyMessage={
                <EmptyState
                  title={t('location.cities.empty.title')}
                  description={t('location.cities.empty.desc')}
                />
              }
            >
              {cityColumns({
                t,
                localized,
                busyId: cityBusyId,
                onEdit: openEditCity,
                onToggle: onToggleCity,
                onDelete: onDeleteCity,
              })}
            </DataTable>
          )
        ) : (
          <InfoBanner
            icon="pi pi-info-circle"
            title={t('location.cities.selectCountryHint.title')}
            description={t('location.cities.selectCountryHint.desc')}
          />
        )}
      </div>
    </div>
  )
}
